import { computed, ref, watch } from 'vue';
import { useQueries, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useApi } from '@shopexperts/shared';
import { useAuthStore } from '@/store/auth';
import { aiSummariesErrorMessage } from './aiSummaries';

export const MAX_TRACKED_BATCHES = 50;

export function restoreBatchIds(value: string | null): string[] {
  try {
    const parsed: unknown = JSON.parse(value ?? '[]');
    return Array.isArray(parsed)
      ? [...new Set(parsed.filter((id): id is string => typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)))]
      : [];
  } catch { return []; }
}

export function useExpertAiBatches() {
  const api = useApi();
  const auth = useAuthStore();
  const queryClient = useQueryClient();
  const ids = ref<string[]>([]);
  const storageError = ref('');
  const storageKey = computed(() => `expert-ai-batches:${auth.user?.id ?? 'anonymous'}`);
  watch(storageKey, (key) => {
    try { ids.value = restoreBatchIds(localStorage.getItem(key)); }
    catch { ids.value = []; storageError.value = 'Job tracking could not be restored from this browser.'; }
  }, { immediate: true });

  function pruneFinishedBatches() {
    let excess = ids.value.length - MAX_TRACKED_BATCHES;
    if (excess <= 0) return false;
    const retained = ids.value.filter((batchId) => {
      const batch = queryClient.getQueryData<{ status: string }>(['admin', auth.user?.id, 'expert-ai-batch', batchId]);
      // Unknown jobs may still be active: only prune confirmed terminal states.
      if (excess > 0 && batch && ['completed', 'failed'].includes(batch.status)) {
        excess--;
        return false;
      }
      return true;
    });
    if (retained.length === ids.value.length) return false;
    ids.value = retained;
    return true;
  }

  function save() {
    pruneFinishedBatches();
    try {
      localStorage.setItem(storageKey.value, JSON.stringify(ids.value));
      storageError.value = '';
    } catch {
      storageError.value = 'Jobs are running, but this browser could not save tracking for refresh.';
    }
  }
  function track(batchId: string) {
    if (ids.value.includes(batchId)) return;
    ids.value = [...ids.value, batchId];
    save(); // Persist each accepted batch immediately, before queueing the next chunk.
  }
  function dismiss(batchId: string) {
    ids.value = ids.value.filter((id) => id !== batchId);
    save();
  }

  const activeBatches = useQuery({
    queryKey: computed(() => ['admin', auth.user?.id, 'expert-ai-active-batches']),
    queryFn: () => api.admin.expertAiSummaries.listActiveBatches(),
    refetchInterval: 3000,
    retry: 1,
  });
  watch(activeBatches.data, (batches) => {
    if (!batches) return;
    for (const batch of batches) {
      queryClient.setQueryData(['admin', auth.user?.id, 'expert-ai-batch', batch.batch_id], batch);
    }
    const merged = [...new Set([...ids.value, ...batches.map((batch) => batch.batch_id)])];
    if (merged.length !== ids.value.length || merged.some((id, index) => id !== ids.value[index])) {
      ids.value = merged;
      save();
    }
  }, { immediate: true });
  const discoveryError = computed(() => activeBatches.error.value
    ? `Could not restore active jobs: ${aiSummariesErrorMessage(activeBatches.error.value)} Retrying automatically.` : '');
  const activeIds = computed(() => new Set(activeBatches.error.value
    ? [] : activeBatches.data.value?.map((batch) => batch.batch_id) ?? []));

  const queries = useQueries({
    queries: computed(() => ids.value.map((batchId) => ({
      queryKey: ['admin', auth.user?.id, 'expert-ai-batch', batchId],
      queryFn: () => api.admin.expertAiSummaries.getBatch({ params: { batchId } }),
      enabled: !activeIds.value.has(batchId),
      retry: 1,
      refetchInterval: (query: { state: { data?: { status: string } } }) =>
        query.state.data && ['completed', 'failed'].includes(query.state.data.status) ? false : 3000,
    }))),
  });
  watch(() => queries.value.map((query) => query.data?.status), () => {
    if (pruneFinishedBatches()) save();
  });
  const jobs = computed(() => queries.value.map((query, index) => {
    const data = query.data;
    const steps = data?.experts.flatMap((expert) => expert.steps) ?? [];
    return {
      id: ids.value[index]!,
      data,
      error: query.error ? aiSummariesErrorMessage(query.error) : '',
      finished: Boolean(data && ['completed', 'failed'].includes(data.status)),
      finishedSteps: steps.filter((step) => ['succeeded', 'failed', 'skipped'].includes(step.status)).length,
      totalSteps: steps.length,
      activeSteps: data?.experts.flatMap((expert) => expert.steps
        .filter((step) => step.status === 'running')
        .map((step) => ({ expertId: expert.expert_id, step: step.step }))) ?? [],
    };
  }));
  return { jobs, track, dismiss, storageError, discoveryError };
}
